import { z } from 'zod';

/**
 * Login form validation schema — uses a single "contact" field (email or phone)
 */
export const loginSchema = z.object({
  contact: z
    .string()
    .min(1, 'Please enter your email or cell number')
    .refine(
      (val) => {
        const trimmed = val.trim();
        // Check if it looks like an email
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return true;
        // Check if it looks like a phone number (7-15 digits after stripping)
        const digits = trimmed.replace(/\D/g, '');
        return digits.length >= 7 && digits.length <= 15;
      },
      { message: 'Please enter a valid email address or phone number' }
    ),
  countryCode: z.string(),
});

export type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Registration form validation schema — email + phone both required
 */
export const registerSchema = z.object({
  firstName: z
    .string()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name is too long')
    .regex(/^[\p{L}\s'-]+$/u, 'First name can only contain letters'),

  lastName: z
    .string()
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name is too long')
    .regex(/^[\p{L}\s'-]+$/u, 'Last name can only contain letters'),

  email: z
    .string()
    .min(1, 'Email address is required')
    .email('Please enter a valid email address'),

  countryCode: z.string(),

  phoneNumber: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true;
        const digits = val.replace(/\D/g, '');
        return digits.length >= 7 && digits.length <= 15;
      },
      {
        message: 'Please enter a valid phone number',
      }
    ),

  agreeToTerms: z
    .boolean()
    .refine((val) => val === true, {
      message: 'Please accept the terms and conditions to continue',
    }),
});

export type RegisterFormData = z.infer<typeof registerSchema>;

/**
 * OTP verification schema
 */
export const otpSchema = z.object({
  code: z
    .string()
    .length(6, 'Must be 6 digits')
    .regex(/^\d{6}$/, 'Numbers only'),
});

export type OTPFormData = z.infer<typeof otpSchema>;

/**
 * Onboarding profile completion schema
 */
export const onboardingProfileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'Required')
    .min(2, 'Too short')
    .max(50, 'Too long')
    .regex(/^[\p{L}\s'-]+$/u, 'Can only contain letters'),

  lastName: z
    .string()
    .min(1, 'Required')
    .min(2, 'Too short')
    .max(50, 'Too long')
    .regex(/^[\p{L}\s'-]+$/u, 'Can only contain letters'),

  countryCode: z.string().min(1, 'Required'),

  phoneNumber: z
    .string()
    .min(10, 'Invalid')
    .max(15, 'Invalid')
    .regex(/^\d+$/, 'Numbers only'),
});

export type OnboardingProfileData = z.infer<typeof onboardingProfileSchema>;

/**
 * Onboarding preferences schema
 */
export const onboardingPreferencesSchema = z.object({
  notifications: z.boolean().optional(),
  homeType: z.string().optional(),
});

export type OnboardingPreferencesData = z.infer<typeof onboardingPreferencesSchema>;
